import { styled } from '@mui/material';
import colors from '@o2/ui/theme/colors';
import { ComponentPropsWithoutRef, ComponentPropsWithRef, ElementType, forwardRef, ReactElement } from 'react';
import { DisplayNameObj } from '@o2/ui';

const ButtonRoot = styled('button', {
  name: 'AppBarInternal',
  slot: 'button',
})(({ theme }) => ({
  background: 'none',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.common.white,
  gap: theme.spacing(2),
  fontFamily: theme.typography.fontFamily,
  fontSize: 14,
  fontWeight: 400,
  textDecoration: 'none',
  cursor: 'pointer',
  minHeight: 32,
  whiteSpace: 'normal',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  transition: 'background-color 500ms ease 0s',
  '&:hover': {
    backgroundColor: colors.transparentWhite['100'],
  },
  padding: theme.spacing(0, 1.5),
}));

export type ButtonProps<T extends ElementType = 'button'> = Parameters<typeof ButtonRoot>[0] & {
  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  as?: T;

  ref?: ComponentPropsWithRef<T>['ref'];
} & ComponentPropsWithoutRef<T>;

const Button: (<T extends ElementType = 'button'>(props: ButtonProps<T>) => ReactElement | null) & DisplayNameObj =
  forwardRef(<T extends ElementType = 'button'>(props: ButtonProps<T>, ref: ButtonProps<T>['ref']) => (
    <ButtonRoot {...props} ref={ref} />
  ));

Button.displayName = 'AppBarInternal.Button';

export default Button;
